import { useState, useEffect } from 'react';
import { adminService, supabase } from '../../services/supabase';

const T = {
  primary:     '#FF4444',
  gradient:    'linear-gradient(135deg, #FF4444, #FF6B00)',
  bg:          '#f5f7fa',
  surface:     '#ffffff',
  border:      '#e5e7eb',
  text:        '#1f2937',
  muted:       '#6b7280',
  red:         '#dc2626',
  redBg:       '#fef2f2',
  redBorder:   '#fecaca',
  green:       '#16a34a',
  greenBg:     '#f0fdf4',
  greenBorder: '#bbf7d0',
};

const AdminLogin = ({ onSuccess }) => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  useEffect(() => {
    document.title = 'Admin Login — Menuron';
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const { data, error } = await adminService.loginAdmin(formData.email, formData.password);

      if (error) {
        setErrors({ general: error.message });
        return;
      }

      localStorage.setItem('menuron_admin_session', data.sessionToken);
      onSuccess(data);

    } catch (error) {
      setErrors({ general: 'Login failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim() || !forgotEmail.includes('@')) {
      setForgotMessage('error:Please enter a valid email address.');
      return;
    }

    setForgotLoading(true);
    setForgotMessage('');

    try {
      // Ensure the admin has a Supabase Auth account (creates one if needed for migration)
      const { ensureAdminInSupabaseAuth } = await import('../../services/authMigration');
      const redirectTo = `${window.location.origin}/admin/reset-password`;
      await ensureAdminInSupabaseAuth(forgotEmail, redirectTo);

      // Send password reset email via Supabase Auth
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.toLowerCase(), {
        redirectTo,
      });

      if (error) throw error;

      setForgotMessage('success:Password reset link sent! Check your email inbox.');
    } catch (error) {
      console.error('Forgot password error:', error);
      // Always show success message to prevent email enumeration
      setForgotMessage('success:If an account exists with this email, a reset link has been sent.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: T.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img
            src="https://i.ibb.co/hF3Q8YrX/menuron-high-resolution-logo.png"
            alt="Menuron"
            style={{ height: '52px', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))', marginBottom: '12px' }}
          />
          <h1 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: '800', color: T.text }}>Admin Portal</h1>
          <p style={{ margin: 0, fontSize: '13px', color: T.muted }}>Menuron System Administration</p>
        </div>

        {/* Card */}
        <div style={{
          background: T.surface, borderRadius: '20px', padding: '32px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: `1px solid ${T.border}`,
        }}>
          {errors.general && (
            <div style={{
              background: T.redBg, border: `1px solid ${T.redBorder}`,
              borderRadius: '8px', padding: '11px 14px', marginBottom: '20px',
              color: T.red, fontSize: '13px', fontWeight: '600',
            }}>{errors.general}</div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: T.text, marginBottom: '6px' }}>Email</label>
              <input
                type="email" name="email" placeholder="admin@menuron.com"
                value={formData.email} onChange={handleInputChange}
                style={{
                  width: '100%', padding: '11px 14px', boxSizing: 'border-box',
                  border: `1.5px solid ${errors.email ? T.red : T.border}`,
                  borderRadius: '10px', fontSize: '14px', outline: 'none', color: T.text,
                }}
                onFocus={e => e.target.style.borderColor = T.primary}
                onBlur={e => e.target.style.borderColor = errors.email ? T.red : T.border}
              />
              {errors.email && <div style={{ color: T.red, fontSize: '11px', marginTop: '4px' }}>{errors.email}</div>}
            </div>

            <div style={{ marginBottom: '6px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: T.text, marginBottom: '6px' }}>Password</label>
              <input
                type="password" name="password" placeholder="••••••••"
                value={formData.password} onChange={handleInputChange}
                style={{
                  width: '100%', padding: '11px 14px', boxSizing: 'border-box',
                  border: `1.5px solid ${errors.password ? T.red : T.border}`,
                  borderRadius: '10px', fontSize: '14px', outline: 'none', color: T.text,
                }}
                onFocus={e => e.target.style.borderColor = T.primary}
                onBlur={e => e.target.style.borderColor = errors.password ? T.red : T.border}
              />
              {errors.password && <div style={{ color: T.red, fontSize: '11px', marginTop: '4px' }}>{errors.password}</div>}
            </div>

            <div style={{ textAlign: 'right', marginBottom: '22px' }}>
              <button type="button"
                onClick={() => { setForgotEmail(formData.email); setShowForgotPassword(true); setForgotMessage(''); }}
                style={{ background: 'none', border: 'none', color: T.primary, fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: '4px 0' }}>
                Forgot Password?
              </button>
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', background: loading ? T.muted : T.gradient,
              color: 'white', border: 'none', borderRadius: '10px',
              padding: '13px', fontSize: '14px', fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 16px rgba(99,102,241,0.35)',
            }}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', fontSize: '11px', color: T.muted, marginTop: '20px' }}>
          Authorized Personnel Only
        </p>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: T.surface, borderRadius: '20px', padding: '28px',
            maxWidth: '420px', width: '90%',
            boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: '800', color: T.text }}>Reset Password</h3>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: T.muted }}>
              Enter your admin email and we'll send a reset link.
            </p>

            {forgotMessage && (
              <div style={{
                background: forgotMessage.startsWith('success:') ? T.greenBg : T.redBg,
                border: `1px solid ${forgotMessage.startsWith('success:') ? T.greenBorder : T.redBorder}`,
                borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
                color: forgotMessage.startsWith('success:') ? T.green : T.red,
                fontSize: '13px', fontWeight: '600',
              }}>
                {forgotMessage.replace(/^(success:|error:)/, '')}
              </div>
            )}

            <input
              type="email" placeholder="Admin email address"
              value={forgotEmail}
              onChange={e => setForgotEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
              style={{
                width: '100%', padding: '11px 14px', boxSizing: 'border-box',
                border: `1.5px solid ${T.border}`, borderRadius: '10px',
                fontSize: '14px', outline: 'none', marginBottom: '20px', color: T.text,
              }}
              onFocus={e => e.target.style.borderColor = T.primary}
              onBlur={e => e.target.style.borderColor = T.border}
            />

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowForgotPassword(false); setForgotMessage(''); }}
                style={{ padding: '9px 18px', border: `1.5px solid ${T.border}`, background: 'transparent', color: T.muted, borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                Cancel
              </button>
              <button
                onClick={handleForgotPassword} disabled={forgotLoading}
                style={{
                  padding: '9px 18px', background: forgotLoading ? T.muted : T.gradient,
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: forgotLoading ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: '700',
                }}>
                {forgotLoading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogin;
